import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  handleVisibleForEdit as handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { updateReceiving } from "../../Actions/ReceivingAction";
import { getCompany } from "../../Actions/CompanyAction";
import ReceivingForm, { emptyLine } from "./ReceivingForm";

const num = (value) =>
  value === undefined || value === null ? "" : String(value);

/** A saved line's item is populated; a freshly built one carries only an id. */
const itemIdOf = (line) => {
  const value = line?.itemId;
  if (!value) return "";
  return typeof value === "object" ? value._id : String(value);
};

/**
 * Editing adjusts the stock ledger to match the new lines - a quantity that
 * grew writes the extra, one that shrank or a line that was removed writes the
 * reversal that gives the stock back. All of that is server-side.
 */
const EditReceiving = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Memoised because the `|| {}` fallback would otherwise be a fresh object on
  // every render, re-running the useMemo below and re-seeding the form under
  // the user as they type.
  const receiving = useMemo(() => location.state || {}, [location.state]);

  const isUpdatingReceiving = useSelector(
    (state) => state.ReceivingReducer?.isUpdatingReceiving,
  );

  useEffect(() => {
    dispatch(getCompany("", "", ""));
  }, [dispatch]);

  const initial = useMemo(() => {
    const lines = (receiving.items || []).map((line, index) => ({
      // The line's own _id is kept so the ledger keeps reconciling against the
      // same line rather than reversing it and adding it back.
      _key: `saved-${line._id || index}`,
      _id: line._id,
      itemId: itemIdOf(line),
      itemName: line.itemName || "",
      hsCode: line.hsCode || "",
      uom: line.uom || "",
      quantity: num(line.quantity),
      unitCost: line.unitCost ? num(line.unitCost) : "",
    }));
    return {
      receivingNumber: receiving.receivingNumber || "",
      date: receiving.date || "",
      supplierName: receiving.supplierName || "",
      referenceNo: receiving.referenceNo || "",
      note: receiving.note || "",
      companyId: receiving.companyId?._id || "",
      items: lines.length > 0 ? lines : [emptyLine()],
    };
  }, [receiving]);

  const handleSubmit = (payload) => {
    dispatch(
      updateReceiving(
        receiving._id,
        payload,
        handleVisible,
        handleDangerVisible,
        navigate,
      ),
    );
  };

  return (
    <ReceivingForm
      initial={initial}
      submitLabel="Edit Receiving"
      submitting={isUpdatingReceiving}
      onSubmit={handleSubmit}
    />
  );
};

export default EditReceiving;
